import type { FeatureVisibilityConfig } from '@/services/featureVisibilityService';

export const PRODUCT_CAT_FILTER_FIELDS = [
  'cat_1',
  'cat_2',
  'cat_3',
  'cat_4',
  'cat_5',
  'cat_6',
] as const;

export type ProductCatFilterField = (typeof PRODUCT_CAT_FILTER_FIELDS)[number];

export const PRODUCT_CAT_FILTER_ACTION_KEYS = PRODUCT_CAT_FILTER_FIELDS.map(
  (field) => `filters.${field}` as const,
);

export type ProductCatFilterOptions = Partial<
  Record<ProductCatFilterField, Array<{ value: string; label: string }>>
>;

export type CatFilterAllFlag = `${ProductCatFilterField}_all`;

export const CAT_FILTER_ALL_FLAG: Record<ProductCatFilterField, CatFilterAllFlag> =
  Object.fromEntries(
    PRODUCT_CAT_FILTER_FIELDS.map((field) => [field, `${field}_all`]),
  ) as Record<ProductCatFilterField, CatFilterAllFlag>;

export function normalizeCatFilterValues(
  value: string | string[] | undefined,
): string[] {
  if (value === undefined) return [];
  const raw = Array.isArray(value) ? value : [value];
  return raw.map((v) => String(v).trim()).filter(Boolean);
}

export function isCatFieldAllSelected(
  field: ProductCatFilterField,
  options: {
    allFlags?: Partial<Record<CatFilterAllFlag, boolean>>;
    selectAllByFilter?: Record<string, boolean>;
  },
): boolean {
  const allFlag = CAT_FILTER_ALL_FLAG[field];
  if (options.allFlags?.[allFlag]) return true;
  if (options.selectAllByFilter?.[field]) return true;
  return false;
}

export function productMatchesCatFilter(
  product: Partial<Record<ProductCatFilterField, string | null | undefined>>,
  field: ProductCatFilterField,
  options: {
    valuesByField: Partial<Record<ProductCatFilterField, string | string[]>>;
    allFlags?: Partial<Record<CatFilterAllFlag, boolean>>;
    selectAllByFilter?: Record<string, boolean>;
  },
): boolean {
  const productValue = product[field];
  if (isCatFieldAllSelected(field, options)) {
    return productValue != null && String(productValue).trim() !== '';
  }
  const values = normalizeCatFilterValues(options.valuesByField[field]);
  if (values.length === 0) {
    return true;
  }
  return productValue != null && values.includes(String(productValue));
}

export type ProductCatApiParams = {
  [K in ProductCatFilterField]?: string | string[];
} & {
  [K in CatFilterAllFlag]?: boolean;
};

export function buildProductCatApiParams(
  valuesByField: Partial<Record<ProductCatFilterField, string | string[]>>,
  visibleFields: ProductCatFilterField[],
  isAllSelected: (field: ProductCatFilterField) => boolean,
): ProductCatApiParams {
  const params: ProductCatApiParams = {};
  for (const field of visibleFields) {
    if (isAllSelected(field)) {
      params[CAT_FILTER_ALL_FLAG[field]] = true;
      continue;
    }
    const values = normalizeCatFilterValues(valuesByField[field]);
    if (values.length > 0) {
      params[field] = values;
    }
  }
  return params;
}

export function hasActiveCatScopeFilter(
  valuesByField: Partial<Record<ProductCatFilterField, string | string[]>>,
  allFlags?: Partial<Record<CatFilterAllFlag, boolean>>,
  selectAllByFilter?: Record<string, boolean>,
): boolean {
  return PRODUCT_CAT_FILTER_FIELDS.some((field) => {
    if (isCatFieldAllSelected(field, { allFlags, selectAllByFilter })) {
      return true;
    }
    return normalizeCatFilterValues(valuesByField[field]).length > 0;
  });
}

export function isProductCatFilterEnabled(
  config: FeatureVisibilityConfig,
  field: ProductCatFilterField,
): boolean {
  const key = `filters.${field}`;
  const direct = config.actions?.[key]?.enabled;
  if (direct !== undefined) return direct;
  if (field === 'cat_1') {
    const legacy = config.actions?.['filters.category']?.enabled;
    if (legacy !== undefined) return legacy;
  }
  return false;
}

export function getVisibleProductCatFilters(
  config: FeatureVisibilityConfig,
): ProductCatFilterField[] {
  return PRODUCT_CAT_FILTER_FIELDS.filter((field) => isProductCatFilterEnabled(config, field));
}

export function appendProductCatParams(
  target: Record<string, string | number | boolean | string[]>,
  filters: Record<string, string[] | undefined> | undefined,
  visibleFields: ProductCatFilterField[],
): void {
  if (!filters) return;
  for (const field of visibleFields) {
    const values = filters[field];
    if (!values?.length) continue;
    target[field] = values;
  }
}

export function appendProductCatRequestParams(
  target: Record<string, string | number | boolean | string[] | number[]>,
  params?: Partial<Record<ProductCatFilterField | CatFilterAllFlag, string | string[] | boolean>>,
): void {
  for (const field of PRODUCT_CAT_FILTER_FIELDS) {
    const allFlag = CAT_FILTER_ALL_FLAG[field];
    if (params?.[allFlag] === true) {
      target[allFlag] = true;
      continue;
    }
    const raw = params?.[field];
    if (raw === undefined || raw === null) continue;
    if (Array.isArray(raw)) {
      const values = raw.map((value) => String(value).trim()).filter(Boolean);
      if (values.length > 0) {
        target[field] = values;
      }
      continue;
    }
    const trimmed = String(raw).trim();
    if (trimmed) {
      target[field] = trimmed;
    }
  }
}

export function appendProductCatQueryParams(
  target: Record<string, string | number>,
  valuesByField: Partial<Record<ProductCatFilterField, string[] | number[] | undefined>>,
  visibleFields: ProductCatFilterField[],
): void {
  for (const field of visibleFields) {
    const raw = valuesByField[field];
    if (!raw?.length) continue;
    const values = raw.map((v) => String(v)).filter(Boolean);
    if (values.length) {
      target[field] = values.join(',');
    }
  }
}
